import { IconGitHub } from "@/assets/icons/geist/IconGitHub";
import { IconGitLab } from "@/assets/icons/geist/IconGitLab";
import { IconGit } from "@/assets/icons/geist/IconGit";
import { WebhookProvider, WebhookType } from "gitpod-next-api/gitpod/v1/webhook_pb";
import type { FC } from "react";
import type { BaseIconProps } from "@/assets/icons/utils";

/**
 * Display configuration for webhook SCM providers.
 * Maps WebhookProvider enum values to their corresponding icon component and display name.
 */
export const WEBHOOK_PROVIDER_CONFIG: Record<WebhookProvider, { icon: FC<BaseIconProps>; name: string }> = {
    [WebhookProvider.GITHUB]: { icon: IconGitHub, name: "GitHub" },
    [WebhookProvider.GITLAB]: { icon: IconGitLab, name: "GitLab" },
    [WebhookProvider.UNSPECIFIED]: { icon: IconGit, name: "Unknown" },
};

/**
 * Display configuration for webhook scope types.
 * Maps WebhookType enum values to their display name.
 */
export const WEBHOOK_TYPE_CONFIG: Record<WebhookType, { name: string }> = {
    [WebhookType.SCM_REPOSITORY]: { name: "Repository" },
    [WebhookType.SCM_ORGANIZATION]: { name: "Organization" },
    [WebhookType.UNSPECIFIED]: { name: "Unknown" },
};

/**
 * Returns the URL to the webhook settings page for the given provider and scope.
 * @param isNewWebhook - If true, returns URL for creating a new webhook (includes /new for GitHub)
 * @param scmHost - The actual SCM host to use. Falls back to the default host for the provider.
 */
export const getWebhookSettingsUrl = (
    provider: WebhookProvider,
    type: WebhookType,
    scope: string,
    isNewWebhook = false,
    scmHost?: string,
): string => {
    if (!scope) return "";

    if (provider === WebhookProvider.GITHUB) {
        const host = scmHost || "github.com";
        const suffix = isNewWebhook ? "/new" : "";
        if (type === WebhookType.SCM_ORGANIZATION) {
            return `https://${host}/organizations/${scope}/settings/hooks${suffix}`;
        }
        return `https://${host}/${scope}/settings/hooks${suffix}`;
    }

    if (provider === WebhookProvider.GITLAB) {
        const host = scmHost || "gitlab.com";
        if (type === WebhookType.SCM_ORGANIZATION) {
            return `https://${host}/groups/${scope}/-/hooks`;
        }
        return `https://${host}/${scope}/-/hooks`;
    }

    return "";
};

/**
 * A webhook is fully configured when it has an organization scope, at least one
 * repository scope, or a non-empty legacy scope string.
 * Webhooks created via the dialog but where the user clicked "Do later"
 * on step 2 will have an empty scope.
 */
export const isWebhookFullyConfigured = (webhook: {
    spec?: {
        scope?: string;
        scopes?: { host?: string; owner?: string; name?: string }[];
        organizationScope?: { host?: string; name?: string };
        type?: WebhookType;
        provider?: WebhookProvider;
    };
}): boolean => {
    if (webhook.spec?.organizationScope) {
        return true;
    }
    if (webhook.spec?.scopes && webhook.spec.scopes.length > 0) {
        return true;
    }
    return !!webhook.spec?.scope;
};

/**
 * Extracts owner/repo path from a full URL like "https://github.com/owner/repo"
 */
export const extractRepoPath = (url: string): string => {
    try {
        const urlObj = new URL(url);
        return urlObj.pathname.slice(1); // Remove leading slash
    } catch {
        return url;
    }
};

/**
 * Returns the default SCM host for a given provider.
 * Only use as a fallback when no SCM integrations are available.
 */
export const getDefaultScmHost = (provider: WebhookProvider): string => {
    switch (provider) {
        case WebhookProvider.GITHUB:
            return "github.com";
        case WebhookProvider.GITLAB:
            return "gitlab.com";
        default:
            return "";
    }
};

/**
 * Maps a WebhookProvider to the corresponding runner SCM integration scmId.
 */
export const providerToScmId = (provider: WebhookProvider): string => {
    switch (provider) {
        case WebhookProvider.GITHUB:
            return "github";
        case WebhookProvider.GITLAB:
            return "gitlab";
        default:
            return "";
    }
};

/**
 * Returns scope-related configuration based on webhook type.
 */
export const getScopeConfig = (
    type: WebhookType,
): { isOrgType: boolean; scopeLabel: string; comboboxPlaceholder: string } => {
    switch (type) {
        case WebhookType.SCM_ORGANIZATION:
            return {
                isOrgType: true,
                scopeLabel: "Organization",
                comboboxPlaceholder: "Select an organization",
            };
        case WebhookType.SCM_REPOSITORY:
            return {
                isOrgType: false,
                scopeLabel: "Repository",
                comboboxPlaceholder: "Select a repository",
            };
        default:
            return {
                isOrgType: false,
                scopeLabel: "Organization",
                comboboxPlaceholder: "Select an organization",
            };
    }
};

/**
 * Represents an SCM repository with name and URL.
 */
export type SCMRepository = {
    name: string;
    url: string;
};

/**
 * Converts an SCM repository URL into a WebhookRepositoryScope-compatible object.
 * Derives the host from the repo URL rather than assuming a default.
 */
export const scmRepoToScope = (
    repo: SCMRepository,
): { host: string; owner: string; name: string } => {
    const host = extractHost(repo.url);
    const path = extractRepoPath(repo.url);
    const parts = path.split("/");
    const owner = parts[0] || "";
    const name = parts[1] || "";
    return {
        host,
        owner,
        name,
    };
};

/**
 * Extracts the hostname from a URL.
 */
export const extractHost = (url: string): string => {
    try {
        return new URL(url).hostname;
    } catch {
        return "";
    }
};

/**
 * Converts a WebhookRepositoryScope to a display string ("owner/name").
 */
export const scopeToDisplayString = (scope: { owner?: string; name?: string }): string => {
    return `${scope.owner || ""}/${scope.name || ""}`;
};
